import { Route, Routes, useLocation } from 'react-router-dom';
import { Fragment, lazy, Suspense } from 'react';
import DocumentTitle from './components/DocumentTitle';
import Logo from './components/Logo';
import CompetitionDeviceGate from './components/CompetitionDeviceGate';
import {
  LayoutPreviewHome,
  LayoutPreviewNoSessionScreen,
  LayoutPreviewParticipantShell,
  LayoutPreviewScreenShell,
  LayoutPreviewVoteShell,
} from './routes/layoutPreview/LayoutPreview';

// Pagine immersive: niente watermark globale.
const ROUTES_WITHOUT_GLOBAL_LOGO = new Set<string>([
  '/',          // landing pubblica — ha il proprio logo/nav
  '/login',
  '/admin',
  '/timer',
  '/theme',
  '/fullview',
  '/vote',
  '/participant',
  '/enable-device',
  '/selfie',
]);

function shouldHideGlobalLogo(pathname: string): boolean {
  if (pathname.startsWith('/screen')) return true;
  if (pathname.startsWith('/dev/layout')) return true;
  return ROUTES_WITHOUT_GLOBAL_LOGO.has(pathname);
}

const Home = lazy(() => import('./routes/Home'));
const Admin = lazy(() => import('./routes/Admin'));
const Participant = lazy(() => import('./routes/Participant'));
const ScreenLayout = lazy(() => import('./routes/screen/ScreenLayout'));
const ScreenSlotsView = lazy(() => import('./routes/screen/ScreenSlotsView'));
const ScreenTimerView = lazy(() => import('./routes/screen/ScreenTimerView'));
const ScreenThemeView = lazy(() => import('./routes/screen/ScreenThemeView'));
const Vote = lazy(() => import('./routes/Vote'));
const Timer = lazy(() => import('./routes/Timer'));
const Theme = lazy(() => import('./routes/Theme'));
const Fullview = lazy(() => import('./routes/Fullview'));
const TestApi = lazy(() => import('./routes/TestApi'));
const Login = lazy(() => import('./routes/Login'));
const Selfie = lazy(() => import('./routes/Selfie'));
const EnableDevice = lazy(() => import('./routes/EnableDevice'));
const NotFound = lazy(() => import('./routes/NotFound'));
const Landing = lazy(() => import('./routes/Landing'));

export default function App() {
  const location = useLocation();
  const showGlobalLogo = !shouldHideGlobalLogo(location.pathname);

  return (
    <>
      <DocumentTitle />
      {showGlobalLogo ? <Logo size="medium" /> : null}
      <Suspense fallback={<div className="appLoading">CARICAMENTO...</div>}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/vote" element={<Vote />} />
          <Route path="/selfie" element={<Selfie />} />
          <Route path="/enable-device" element={<EnableDevice />} />
          <Route element={<CompetitionDeviceGate />}>
            <Route path="/hub" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/participant" element={<Participant />} />
            <Route path="/screen" element={<ScreenLayout />}>
              <Route index element={<ScreenSlotsView />} />
              <Route path="no-session" element={<Fragment />} />
              <Route path="timer" element={<ScreenTimerView />} />
              <Route path="theme" element={<ScreenThemeView />} />
            </Route>
            <Route path="/timer" element={<Timer />} />
            <Route path="/theme" element={<Theme />} />
            <Route path="/fullview" element={<Fullview />} />
            <Route path="/testapi" element={<TestApi />} />
            <Route path="/dev/layout" element={<LayoutPreviewHome />} />
            <Route path="/dev/layout/screen/no-session" element={<LayoutPreviewNoSessionScreen />} />
            <Route path="/dev/layout/screen/:presetId" element={<LayoutPreviewScreenShell />}>
              <Route index element={<ScreenSlotsView />} />
              <Route path="timer" element={<ScreenTimerView />} />
              <Route path="theme" element={<ScreenThemeView />} />
            </Route>
            <Route path="/dev/layout/participant/:presetId" element={<LayoutPreviewParticipantShell />} />
            <Route path="/dev/layout/vote/:presetId" element={<LayoutPreviewVoteShell />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}
